import React, { useState } from 'react';
import { Button, Typography } from 'ck-ui';
import { getStoreLocation } from 'ck-utils';
import { addBusinessDays } from 'date-fns';
import styles from './ShippingEstimator.module.scss';

const deliveryMethod = {
  AU: [
    { id: 1, name: 'Standard', from: 3, to: 5, cost: '$9.95 or FREE over $100' },
    { id: 2, name: 'Express', from: 2, to: 4, cost: '$12.95' },
    { id: 3, name: 'Jump the Queue', from: 1, to: 3, cost: '$30.00' }
  ],
  NZ: [
    { id: 1, name: 'Standard', from: 4, to: 6, cost: '$9.95 or Free over $100' },
    { id: 2, name: 'Express', from: 2, to: 3, cost: '$14.95 or Free over $200' }
  ],
  US: [
    { id: 1, name: 'Standard Shipping', from: 3, to: 7, cost: '$9.99 or FREE over $100' },
    { id: 2, name: 'Express Shipping', from: 2, to: 3, cost: '$14.99' },
    { id: 3, name: 'Overnight Shipping', from: 1, to: 2, cost: '$24.99' }
  ]
};

const ShippingEstimator = () => {
  const [postcode, setPostcode] = useState('');
  const [isShowEstimatedTimeFrame, setIsShowEstimatedTimeFrame] = useState(false);
  let storeLocation = getStoreLocation();
  if (!['AU', 'NZ', 'US'].includes(storeLocation)) {
    storeLocation = 'AU';
  }

  const settings = { maxLength: storeLocation === 'US' ? 10 : 4,
    postcodeRegex: storeLocation === 'US' ? /^[0-9]{5}(-[0-9]{4})?$/ : /^[0-9]{4}$/,
    locale: `en-${storeLocation}`,
    inputCheckRegex: storeLocation === 'US' ? /^$|^\d+(-\d+)*-?$/ : /^$|^\d+$/,
    data: deliveryMethod[storeLocation] };

  const shippingInfo = (
    <Typography variant="body" style={{ marginTop: 30 }}>
      *See our shipping information page for{' '}
      <Typography color="danger" renderAs="a" variant="body" underline href="/pages/shipping-returns">
        international shipping methods & timeframes
      </Typography>
    </Typography>
  );

  const onClick = () => {
    setIsShowEstimatedTimeFrame(true);
    window.dataLayer.push({
      event: 'gaEvent',
      gaEvent: {
        name: 'shipping_estimator_click',
        category: 'PDP Delivery',
        action: 'click',
        label: postcode,
      },
    });
  };

  const isValid = settings.inputCheckRegex.test(postcode);

  const handlePostcodeChange = (event) => {
    setPostcode(event.target.value);
  };

  const reset = () => {
    setIsShowEstimatedTimeFrame(false);
  };

  if (isShowEstimatedTimeFrame) {
    const today = new Date();
    // Format the dates as "Fri 5 May"
    const options = { weekday: 'short', day: 'numeric', month: 'short' };

    return (
      <>
        <div className={styles.changeLocation}>
          <Typography renderAs="a" display="block" weight={700} underline variant="body-small" onClick={reset} align="right">
            <font color="darkred">🖊</font> Change Location
          </Typography>
        </div>
        {settings.data.map((item) => {
          const startDate = addBusinessDays(today, item.from).toLocaleDateString(settings.locale, options);
          const endDate = addBusinessDays(today, item.to).toLocaleDateString(settings.locale, options);

          return (
            <div key={item.id} className={styles.deliveryMethod}>
              <div>
                <Typography variant="body" color="black-bis">
                  {postcode} - {item.name}
                </Typography>
                <Typography variant="body-small">
                  Arrives {startDate} - {endDate}
                </Typography>
              </div>
              <Typography weight={700} variant="body" align="right">
                {item.cost}
              </Typography>
            </div>
          );
        }
        )}
        {shippingInfo}
      </>
    );
  }
  return (
    <>
      <Typography variant="body" weight={700}>
        Please enter your postcode for delivery options:
      </Typography>
      <div className={styles.searchInput}>
        <input
          type="text"
          value={postcode}
          placeholder="Enter a postcode"
          className={isValid ? styles.postcode : styles.error}
          maxLength={settings.maxLength}
          onChange={handlePostcodeChange}
        />
        <Button onClick={onClick} disabled={!settings.postcodeRegex.test(postcode)}>Check</Button>
      </div>
      {!isValid
        && <Typography variant="body" color="danger">Cannot find your postcode or invalid characters have been used.</Typography>
      }
      {shippingInfo}
    </>
  );
};

export { ShippingEstimator };
